import { createClient } from "next-sanity"

const projectId = `g1mmyteo`
const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET || `production`
const apiVersion = "2023-05-03"

export const client = createClient({
    projectId,
    dataset,
    apiVersion,
    useCdn: false,
})

/**
 * Fields that must never carry stega encoding: their values are used in
 * logic (URL building, CSS colors, date parsing, tag filtering) where the
 * invisible characters would break things.
 */
const STEGA_DENY_FIELDS = new Set([
    "link",
    "fileURL",
    "url",
    "playbackId",
    "value", // simplerColor { label, value } — value feeds CSS
    "backgroundColor",
    "accentColor",
    "date",
    "endDate",
    "timeOverride",
    "tags",
    "slug",
    "icon",
])

/**
 * Returns the regular client, or a draft-aware client when Next.js
 * draft mode is active (Sanity Presentation live preview).
 * Stega encoding powers the click-to-edit overlays in the Presentation tool.
 * The token stays server-side: this only runs in getStaticProps/API routes.
 */
export function getClient(draftMode?: boolean) {
    if (!draftMode) return client
    return client.withConfig({
        token: process.env.SANITY_API_READ_TOKEN,
        perspective: "drafts",
        apiVersion: "2025-02-19",
        useCdn: false,
        stega: {
            enabled: true,
            studioUrl:
                process.env.NEXT_PUBLIC_SANITY_STUDIO_URL ||
                "http://localhost:3333/production",
            filter: (props) => {
                if (
                    props.sourcePath.some(
                        (segment) =>
                            typeof segment === "string" &&
                            STEGA_DENY_FIELDS.has(segment),
                    )
                ) {
                    return false
                }
                return props.filterDefault(props)
            },
        },
    })
}

/**
 * Builds the CDN URL for a file asset from its reference
 * (e.g. "file-<hash>-svg"), so queries don't need to resolve asset->url.
 */
export function fileUrlFor(ref?: string) {
    if (!ref) return null
    const match = ref.match(/^file-([A-Za-z0-9]+)-(\w+)$/)
    if (!match) return null
    return `https://cdn.sanity.io/files/${projectId}/${dataset}/${match[1]}.${match[2]}`
}

import imageUrlBuilder from "@sanity/image-url"

const builder = imageUrlBuilder(client)

export function urlFor(source: any) {
    return builder.image(source).width(2500).quality(70).format("webp")
}
